import { createRootRoute, Link, Outlet } from '@tanstack/react-router';
import { LogOut, Menu, User, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { AuthProvider, useAuth } from '@/providers/auth-provider';

function RootComponent() {
  return (
    <AuthProvider>
      <RootLayout />
    </AuthProvider>
  );
}

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
}

function NavLink({ to, children }: NavLinkProps) {
  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
        <Link to={to} className="[&.active]:font-bold">
          <span className="flex items-center gap-2">{children}</span>
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
}

function RootLayout() {
  const { session, signOut, status } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = () => {
    signOut();
  };

  const isAdmin = Boolean(session?.user?.isAdmin);
  const navItems = [{ to: '/', label: 'Home' }, ...(isAdmin ? [{ to: '/users', label: 'Heroes' }] : [])];

  return (
    <>
      <header className="border-b-[3px] border-border bg-accent">
        <div className="container mx-auto px-3 py-3 md:px-4 md:py-4 flex justify-between items-center gap-4">
          {/* Wordmark */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-display text-2xl md:text-3xl tracking-wide text-primary [-webkit-text-stroke:1.5px_var(--color-border)] [paint-order:stroke_fill] leading-none">
              Brain&nbsp;of&nbsp;Chris
            </span>
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden md:flex md:mr-auto">
            <NavigationMenuList>
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to}>
                  {item.label}
                </NavLink>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden ml-auto">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="h-10 w-10"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {status === 'authenticated' && session && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md border">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{session.user.name || session.user.email}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="absolute top-0 left-0 right-0 bg-background border-b md:hidden z-50 shadow-lg">
            <div className="container mx-auto px-4 py-3">
              <nav className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                      '[&.active]:bg-accent/50 [&.active]:text-accent-foreground',
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>

      <main className="container mx-auto px-3 py-4 md:px-4 md:py-6">
        <Outlet />
      </main>
    </>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
